import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient, { Evidence } from '../api/client';
import { Upload, X, CheckCircle, AlertCircle, Loader2, Shield, ArrowRight, Files, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { HashDisplay } from '../components/ui/HashDisplay';

const EVIDENCE_TYPES = [
  'Document',
  'Image',
  'Video',
  'Audio',
  'DigitalFile',
];

// File entry with individual metadata and status
interface FileEntry {
  id: string;
  file: File;
  evidenceType: string;
  description: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  uploadedEvidence?: Evidence;
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

function getFileTypeIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎬';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  return '📁';
}

function autoDetectEvidenceType(file: File): string {
  const mimeType = file.type.toLowerCase();
  
  // Must match DB constraint: Document, Image, Video, Audio, DigitalFile
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (mimeType.includes('pdf') || 
      mimeType.startsWith('application/msword') ||
      mimeType.startsWith('application/vnd.openxmlformats') ||
      mimeType.startsWith('text/')) {
    return 'Document';
  }
  return 'DigitalFile';
}

export default function EvidenceUpload() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [caseNumber, setCaseNumber] = useState('');
  const [sharedDescription, setSharedDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploadedResults, setUploadedResults] = useState<Evidence[]>([]);

  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Add files to the queue
  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const entries: FileEntry[] = fileArray.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      evidenceType: autoDetectEvidenceType(file),
      description: '',
      status: 'pending' as const,
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...entries]);
  }, []);

  // Remove a file from the queue
  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Update file metadata
  const updateFile = useCallback((id: string, updates: Partial<FileEntry>) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  }, []);

  // Set evidence type for all files
  const setAllEvidenceTypes = useCallback((type: string) => {
    setFiles((prev) => prev.map((f) => ({ ...f, evidenceType: type })));
  }, []);

  // Upload all files sequentially
  const uploadAllFiles = async () => {
    if (!caseNumber || files.length === 0) return;
    
    setIsUploading(true);
    const results: Evidence[] = [];
    
    for (const fileEntry of files) {
      if (fileEntry.status === 'success') continue; // Skip already uploaded
      
      // Mark as uploading
      updateFile(fileEntry.id, { status: 'uploading', progress: 0 });
      
      try {
        const evidence = await apiClient.uploadEvidence(
          caseNumber,
          fileEntry.file,
          {
            description: fileEntry.description || sharedDescription || `Evidence: ${fileEntry.file.name}`,
            evidenceType: fileEntry.evidenceType,
          },
          (progress) => updateFile(fileEntry.id, { progress })
        );
        
        updateFile(fileEntry.id, {
          status: 'success',
          progress: 100,
          uploadedEvidence: evidence,
        });
        results.push(evidence);
      } catch (error) {
        updateFile(fileEntry.id, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
      }
    }
    
    setIsUploading(false);
    setUploadedResults(results);
    
    if (results.length > 0) {
      setShowSuccessModal(true);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    navigate(`/cases/${caseNumber}`);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0 || !caseNumber) return;
    await uploadAllFiles();
  };

  const pendingFiles = files.filter((f) => f.status === 'pending');
  const hasValidFiles = files.some((f) => f.status === 'pending' && f.evidenceType);
  const canSubmit = hasValidFiles && caseNumber && !isUploading;
  const uploadedCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  // Calculate overall progress
  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + (f.status === 'success' ? 100 : f.progress), 0) / files.length)
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload Evidence</h1>
        <p className="text-gray-600 mt-1">
          Upload one or more evidence files with chain of custody tracking
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File upload area - supports multiple files */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : files.length > 0
              ? 'border-green-500 bg-green-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {files.length > 0 ? (
            <div className="flex items-center justify-center gap-4">
              <Files className="h-12 w-12 text-green-600" />
              <div className="text-left">
                <p className="font-medium text-gray-900">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(files.reduce((sum, f) => sum + f.file.size, 0))} total
                </p>
              </div>
              <label className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 cursor-pointer border border-primary-300 rounded-lg hover:bg-primary-50">
                Add more
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleFileChange}
                />
              </label>
            </div>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mx-auto" />
              <p className="mt-4 text-gray-600">
                Drag and drop files here, or{' '}
                <label className="text-primary-600 hover:text-primary-700 cursor-pointer">
                  browse
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Supports multiple files: Images, Videos, Documents, Audio
              </p>
            </>
          )}
        </div>

        {/* File list with individual metadata */}
        {files.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                Files to Upload ({files.length})
              </h3>
              {pendingFiles.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Set all types to:</span>
                  <select
                    onChange={(e) => e.target.value && setAllEvidenceTypes(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                    defaultValue=""
                  >
                    <option value="">Choose...</option>
                    {EVIDENCE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {files.map((fileEntry) => (
                <div
                  key={fileEntry.id}
                  className={`p-4 ${
                    fileEntry.status === 'success'
                      ? 'bg-green-50'
                      : fileEntry.status === 'error'
                      ? 'bg-red-50'
                      : fileEntry.status === 'uploading'
                      ? 'bg-blue-50'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* File icon */}
                    <span className="text-2xl">{getFileTypeIcon(fileEntry.file.type)}</span>
                    
                    {/* File info and controls */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate" title={fileEntry.file.name}>
                            {fileEntry.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(fileEntry.file.size)}
                          </p>
                        </div>
                        
                        {/* Status indicator */}
                        <div className="flex items-center gap-2">
                          {fileEntry.status === 'success' && (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle className="h-4 w-4" />
                              Uploaded
                            </span>
                          )}
                          {fileEntry.status === 'error' && (
                            <span className="flex items-center gap-1 text-red-600 text-sm">
                              <AlertCircle className="h-4 w-4" />
                              Failed
                            </span>
                          )}
                          {fileEntry.status === 'uploading' && (
                            <span className="flex items-center gap-1 text-blue-600 text-sm">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {fileEntry.progress}%
                            </span>
                          )}
                          {fileEntry.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => removeFile(fileEntry.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Remove file"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Evidence type selector (only for pending files) */}
                      {fileEntry.status === 'pending' && (
                        <div className="mt-2 flex items-center gap-4">
                          <select
                            value={fileEntry.evidenceType}
                            onChange={(e) => updateFile(fileEntry.id, { evidenceType: e.target.value })}
                            className="text-sm border border-gray-300 rounded px-2 py-1 flex-1 max-w-48"
                          >
                            <option value="">Select type...</option>
                            {EVIDENCE_TYPES.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={fileEntry.description}
                            onChange={(e) => updateFile(fileEntry.id, { description: e.target.value })}
                            placeholder="Optional description..."
                            className="text-sm border border-gray-300 rounded px-2 py-1 flex-1"
                          />
                        </div>
                      )}

                      {/* Progress bar for uploading files */}
                      {fileEntry.status === 'uploading' && (
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all"
                            style={{ width: `${fileEntry.progress}%` }}
                          />
                        </div>
                      )}

                      {/* Hash display for uploaded files */}
                      {fileEntry.status === 'success' && fileEntry.uploadedEvidence && (
                        <div className="mt-2 flex items-center gap-2">
                          <Shield className="h-3 w-3 text-green-600" />
                          <span className="text-xs font-mono text-gray-600 truncate">
                            SHA-256: {fileEntry.uploadedEvidence.sha256Hash.substring(0, 16)}...
                          </span>
                        </div>
                      )}

                      {/* Error message */}
                      {fileEntry.status === 'error' && fileEntry.error && (
                        <p className="mt-1 text-xs text-red-600">{fileEntry.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Case selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Case Number *
          </label>
          <select
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
            disabled={isUploading}
          >
            <option value="">Select a case...</option>
            {cases?.map((c) => (
              <option key={c.caseNumber} value={c.caseNumber}>
                {c.caseNumber} - {c.title}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            All uploaded files will be associated with this case
          </p>
        </div>

        {/* Shared description (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Shared Description (Optional)
          </label>
          <textarea
            value={sharedDescription}
            onChange={(e) => setSharedDescription(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Description applied to files without individual descriptions..."
            disabled={isUploading}
          />
        </div>

        {/* Overall upload progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Uploading {files.filter((f) => f.status === 'uploading').length > 0 ? 'file' : 'files'}...
              </span>
              <span className="text-gray-900 font-medium">
                {uploadedCount}/{files.length} complete ({overallProgress}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Submit button */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Uploading {uploadedCount}/{files.length}...
              </>
            ) : uploadedCount > 0 && uploadedCount === files.length ? (
              <>
                <CheckCircle className="h-5 w-5" />
                All Uploaded!
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                Upload {files.length} File{files.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
          {files.length > 0 && !isUploading && (
            <button
              type="button"
              onClick={() => setFiles([])}
              className="px-4 py-3 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Info notice */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Chain of Custody Notice:</strong> Upon upload, each file will be
            automatically hashed using SHA-256 for integrity verification. All access
            and modifications will be logged in the chain of custody record.
          </p>
          <p className="text-sm text-blue-700 mt-2">
            Uploading as: <strong>{user?.name}</strong> ({user?.role})
          </p>
        </div>
      </form>

      {/* Success Modal with Summary of All Uploads */}
      {showSuccessModal && uploadedResults.length > 0 && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={handleCloseSuccess} />
          
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              {/* Success icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              
              {/* Title */}
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
                {uploadedResults.length} File{uploadedResults.length !== 1 ? 's' : ''} Uploaded Successfully
              </h2>
              <p className="text-gray-600 text-center mb-6">
                All evidence has been securely stored with chain of custody tracking.
              </p>
              
              {/* Upload summary stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{uploadedResults.length}</p>
                  <p className="text-xs text-gray-600">Uploaded</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {formatFileSize(uploadedResults.reduce((sum, e) => sum + e.fileSize, 0))}
                  </p>
                  <p className="text-xs text-gray-600">Total Size</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-sm font-mono font-bold text-purple-600">{caseNumber}</p>
                  <p className="text-xs text-gray-600">Case</p>
                </div>
              </div>

              {/* Uploaded files list with hashes - HIGHLIGHTED FOR DEMO */}
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 mb-6 max-h-60 overflow-y-auto">
                {uploadedResults.map((evidence) => (
                  <div key={evidence.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{evidence.fileName}</p>
                        <p className="text-xs text-gray-500">
                          {evidence.evidenceType} • {formatFileSize(evidence.fileSize)}
                        </p>
                      </div>
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        ID: {evidence.id}
                      </span>
                    </div>
                    {/* SHA-256 Hash - PROMINENT DISPLAY FOR DEMO */}
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-800">
                          Cryptographic Fingerprint Generated
                        </span>
                      </div>
                      <HashDisplay 
                        hash={evidence.sha256Hash} 
                        label="SHA-256 Hash"
                        verified={true}
                        size="md"
                        showFull={false}
                        copyable={true}
                      />
                      <p className="text-xs text-green-700 mt-2">
                        This hash proves the file has not been altered since upload
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Chain of custody notice */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg mb-6">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <strong>Chain of Custody Initiated</strong>
                  <p className="mt-1">
                    Each upload has been logged as a custody event. All future access 
                    will be tracked automatically.
                  </p>
                </div>
              </div>
              
              {/* Actions - Include View Custody Chain for demo */}
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      setFiles([]);
                      setUploadedResults([]);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Upload More
                  </button>
                  <Link
                    to={`/cases/${caseNumber}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    View Case
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                {/* View Custody Chain link - important for demo */}
                {uploadedResults.length === 1 && (
                  <Link
                    to={`/custody/${uploadedResults[0].id}`}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-green-300 text-green-700 font-medium rounded-lg hover:bg-green-50 transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    View Custody Chain
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error summary modal (if some uploads failed) */}
      {!isUploading && errorCount > 0 && !showSuccessModal && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">
                {errorCount} file{errorCount !== 1 ? 's' : ''} failed to upload
              </p>
              <p className="text-sm text-red-600 mt-1">
                Check the file list for details.
              </p>
            </div>
            <button
              onClick={() => setFiles((prev) => prev.filter((f) => f.status !== 'error'))}
              className="text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
